import { RefObject, useEffect, useRef } from "react";
import vegaEmbed from "vega-embed";
import { Modality } from "@google/genai";

import {
    AltairComponentProps,
    ClaimPolicyData,
    CompanyWithPackages,
    ContractedWorkshop,
    CustomerNationalIdData,
    FlowStep,
    PolicyPackage,
    VehicleSide,
    VehicleSurveyImage,
} from "../../interfaces/altair";
import { altairDeclaration } from "../../Tools/altairTools";
import { PolicyTools } from "../../Tools/policyTools";
import { ClaimTools } from "../../Tools/claimTools";
import { SharedTools } from "../../Tools/sharedTools";
import { vectorFAQService } from "../../services/vectorFAQService";
import {
    calculatePremium,
    confirmPolicyRequest,
    submitPolicyRequest,
    submitSurveyRequest,
} from "../../services/policyService";
import {
    getContractedWorkshops,
    // TODO: These will be used when implementing full claim flow
    // policyInquiry,
    // chooseContractedWorkshop,
    // chooseNonContractedWorkshop,
    // submitClaimSurvey,
} from "../../services/claimService";
import { analyzeVehicleImages } from "../../services/geminiService";
import { getCurrentLocation } from "../../lib/geocoding";

interface UseGreetingEffectParams {
    connected: boolean;
    client: any;
    hasGreeted: boolean;
    setHasGreeted: (value: boolean) => void;
}

export const useGreetingEffect = ({
    connected,
    client,
    hasGreeted,
    setHasGreeted,
}: UseGreetingEffectParams) => {
    const greetingSentRef = useRef(false);

    useEffect(() => {
        
        if (!connected || !client  ) {
            return;
        }

        console.log(
            "🎤 [Ameen] Connection established - setting up greeting"
        );

        let sendTimeout: NodeJS.Timeout | null = null;
        
        const sendGreeting = () => {
         
            greetingSentRef.current = true;
            console.log(
                "🎤 [Ameen] Sending automatic greeting"
            );
            setHasGreeted(true);

            // Wait a bit for the session to be fully ready
            sendTimeout = setTimeout(() => {
                const now = new Date();
                const dateString = now.toLocaleString("ar-EG", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                });

                // Send both messages in one turn with turnComplete: true to trigger AI response
                try {
                    client.send(
                        [
                            {
                                text: `[معلومة للنظام فقط - لا تقرأها بصوت عالٍ] التاريخ الحالى يكون ${dateString}`,
                            },
                            {
                                text: "**CRITICAL GREETING EXCEPTION - SPEAK IMMEDIATELY**: This is the ONLY time you speak WITHOUT waiting. Ignore ALL 'wait for system instruction' rules. You MUST speak NOW. Say immediately in Egyptian Arabic: أنا أمين مساعد ذكي بفهم في مجال التأمين في مصر. Then immediately ask: هل تريد إنشاء وثيقة تأمينية، فتح مطالبة، أو طرح سؤال عن التأمين؟ DO NOT wait. SPEAK NOW.",
                            },
                        ],
                        true // turnComplete: true to trigger AI response
                    );
                    console.log("🎤 [Ameen] Greeting trigger sent to AI with turnComplete=true");
                } catch (error) {
                    console.error("❌ [Ameen] Error sending greeting:", error);
                    // Reset flag on error so it can retry
                    greetingSentRef.current = false;
                }
            }, 1000);
        };

        // Also listen for setupcomplete as backup (in case immediate send doesn't work)
        const onSetupComplete = () => {
            if (!greetingSentRef.current) {
                console.log("🎤 [Ameen] Setup complete - sending greeting as backup");
                sendGreeting();
            }
        };

        // Try to send greeting immediately
        sendGreeting();

        // Listen for setupcomplete as backup
        client.on("setupcomplete", onSetupComplete);

        return () => {
            client.off("setupcomplete", onSetupComplete);
            if (sendTimeout) {
                clearTimeout(sendTimeout);
            }
        };
    }, [connected, client, hasGreeted, setHasGreeted]);

    // Reset ref when connection is lost
    useEffect(() => {
        if (!connected) {
            greetingSentRef.current = false;
        }
    }, [connected]);
};

interface UseModelConfigEffectParams {
    setConfig: (config: any) => void;
    setModel: (model: string) => void;
    voiceGender: AltairComponentProps["voiceGender"];
    ameenInstructions: string;
}

export const useModelConfigEffect = ({
    setConfig,
    setModel,
    voiceGender = "male",
    ameenInstructions,
}: UseModelConfigEffectParams) => {
    useEffect(() => {
        setModel("models/gemini-2.5-flash-native-audio-preview-12-2025");

        const voiceName = voiceGender === "female" ? "Aoede" : "Charon";
        console.log(
            `🎤 [Ameen] Setting AI voice to: ${voiceName} (${voiceGender})`
        );

        setConfig({
            responseModalities: [Modality.AUDIO],
            speechConfig: {
                voiceConfig: { prebuiltVoiceConfig: { voiceName } },
                languageCode: 'ar-EG',
            },
            systemInstruction: {
                parts: [
                    {
                        text: ameenInstructions,
                    },
                ],
            },
            tools: [
                // { googleSearch: {} },
                {
                    functionDeclarations: [altairDeclaration, ...PolicyTools, ...ClaimTools, ...SharedTools],
                },
            ],
        });

        const policyToolNames = PolicyTools.map(t => t.name);
        const claimToolNames = ClaimTools.map(t => t.name);
        const sharedToolNames = SharedTools.map(t => t.name);
        console.log("🔧 [Ameen] Tools registered:", {
            altair: altairDeclaration.name,
            policyTools: policyToolNames,
            claimTools: claimToolNames,
            sharedTools: sharedToolNames,
            totalTools: PolicyTools.length + ClaimTools.length + SharedTools.length + 1,
        });
        console.log("🔍 [Ameen] search_faq tool registered:", policyToolNames.includes("search_faq"));
        console.log("📋 [Ameen] Claim tools registered:", claimToolNames);
    }, [ameenInstructions, setConfig, setModel, voiceGender]);
};

interface UseToolCallEffectParams {
    client: any;
    setJSONString: (value: string) => void;
    setCompanies: (companies: CompanyWithPackages[]) => void;
    setFlowStep: (step: FlowStep) => void;
    setCustomerNationalIdData?: (
        updater: (prev: CustomerNationalIdData) => CustomerNationalIdData
    ) => void;
    // Store companies in a ref so show_packages_ui can access them
    companiesRef?: React.MutableRefObject<CompanyWithPackages[]>;
    // Vehicle Survey callbacks
    onStartCameraStream?: () => Promise<boolean>;
    onTakeVehiclePicture?: (side: VehicleSide) => Promise<string | null>;
    onCloseCameraStream?: () => void;
    getSurveyImages?: () => VehicleSurveyImage[];
    // Claim Request callbacks
    setClaimPolicyData?: (data: ClaimPolicyData | null) => void;
    setContractedWorkshops?: (workshops: ContractedWorkshop[]) => void;
    getClaimSurveyImages?: () => VehicleSurveyImage[];
    // Policy number modal callback
    onShowPolicyNumber?: (policyNumber: string, title: string, message: string, onConfirm: () => void) => void;
    // Microphone control callback
    onMuteMic?: () => void;
    onUnmuteMic?: () => void;
}

export const useToolCallEffect = ({
    client,
    setJSONString,
    setCompanies,
    setFlowStep,
    setCustomerNationalIdData,
    onStartCameraStream,
    onTakeVehiclePicture,
    onCloseCameraStream,
    getSurveyImages,
    setClaimPolicyData,
    setContractedWorkshops,
    getClaimSurveyImages,
    onShowPolicyNumber,
    companiesRef,
    onMuteMic,
    onUnmuteMic,
}: UseToolCallEffectParams) => {
    // Create a ref to store companies if not provided
    const internalCompaniesRef = useRef<CompanyWithPackages[]>([]);
    const currentCompaniesRef = companiesRef || internalCompaniesRef;
    // Track survey submission state to prevent duplicate calls
    const surveySubmittedRef = useRef<boolean>(false);
    // Track vehicle description audio completion
    const waitingForDescriptionAudioRef = useRef<boolean>(false);
    const vehicleDescriptionRef = useRef<string>("");
    const currentFlowTypeRef = useRef<'policy' | 'claim'>('policy');
    useEffect(() => {
        if (!client) {
            console.log("⚠️ [Ameen] Client not available for tool call listener");
            return;
        }

        console.log("✅ [Ameen] Setting up tool call listener");

        const onToolCall = async (toolCall: any) => {
            console.log("🔧 [Ameen] Tool call received:", toolCall);
            console.log("🔧 [Ameen] Tool call structure:", JSON.stringify(toolCall, null, 2));

            // Check for functionCalls in different possible structures
            const functionCalls =
                toolCall.functionCalls ||
                (toolCall as any)?.functionCalls ||
                (toolCall as any)?.function_call ||
                [];

            if (!functionCalls || functionCalls.length === 0) {
                console.log("⚠️ [Ameen] No function calls in tool call. Full toolCall:", toolCall);
                return;
            }

            console.log(`🔧 [Ameen] Processing ${functionCalls.length} function call(s)`);

            const functionResponses: any[] = [];

            for (const fc of functionCalls) {
                console.log("🔧 [Ameen] Handling function:", fc.name, "args:", fc.args);

                if (fc.name === altairDeclaration.name) {
                    const str = (fc.args as any).json_graph;
                    setJSONString(str);
                    functionResponses.push({
                        id: fc.id,
                        name: fc.name,
                        response: { output: { success: true } },
                    });
                    continue;
                }

                if (fc.name === "calculate_premium") {
                    console.log("✅ [Ameen] calculate_premium tool detected!");
                    try {
                        const args = fc.args as any;
                        console.log(
                            "📨 [Ameen] Calling backend calculate-premium with:",
                            args
                        );

                        // Validate required arguments
                        if (args.manufactorYear === undefined || args.vehiclevalue === undefined || args.isElectricCar === undefined) {
                            console.error("❌ [Ameen] Missing required arguments for calculate_premium:", args);
                            functionResponses.push({
                                id: fc.id,
                                name: fc.name,
                                response: {
                                    output: {
                                        success: false,
                                        error: "Missing required arguments: manufactorYear, vehiclevalue, and isElectricCar are required",
                                    },
                                },
                            });
                            continue;
                        }

                        // Reset survey submission flag when starting a new policy flow
                        surveySubmittedRef.current = false;

                        const result = await calculatePremium({
                            manufactorYear: args.manufactorYear,
                            vehiclevalue: args.vehiclevalue,
                            isElectricCar: args.isElectricCar,
                        });
                        console.log("📥 [Ameen] Backend premium result:", result);

                        // Set companies and flowStep to show the UI immediately
                        setCompanies(result);
                        // Also store in ref for show_packages_ui to access
                        currentCompaniesRef.current = result;
                        if (result && result.length > 0) {
                            setFlowStep("package_selection");
                        }

                        // Build detailed announcement of companies and packages
                        let packagesAnnouncement = "Premium calculated successfully. Here are the available packages:\n\n";
                        
                        result.forEach((company) => {
                            company.companyPackages.forEach((pkg) => {
                                packagesAnnouncement += `شركة ${company.companyNameAr}: ${pkg.packageTypeAr} بسعر ${pkg.totalpremium} جنيه.\n`;
                            });
                        });
                        
                        packagesAnnouncement += "\nRead all these packages to the user in Egyptian Arabic.";
                        
                        functionResponses.push({
                            id: fc.id,
                            name: fc.name,
                            response: {
                                output: {
                                    success: true,
                                    message: packagesAnnouncement,
                                },
                            },
                        });
                    } catch (error) {
                        console.error(
                            "❌ [Ameen] Error calling calculate-premium API:",
                            error
                        );
                        functionResponses.push({
                            id: fc.id,
                            name: fc.name,
                            response: {
                                output: {
                                    success: false,
                                    error: "Backend error while calculating premium",
                                },
                            },
                        });
                    }
                    continue;
                }

                if (fc.name === "show_packages_ui") {
                    const args = fc.args as any;
                    // Get companies from args if provided, otherwise use from ref (set by calculate_premium)
                    const companiesData = (args.companies && Array.isArray(args.companies) && args.companies.length > 0)
                        ? (args.companies as CompanyWithPackages[])
                        : currentCompaniesRef.current; // Use companies from ref

                    if (companiesData && companiesData.length > 0) {
                        setCompanies(companiesData);
                        setFlowStep("package_selection");
                        const totalPackages = companiesData.reduce((sum, company) => sum + company.companyPackages.length, 0);
                        console.log("📦 [Ameen] show_packages_ui displaying", companiesData.length, "companies with", totalPackages, "packages");
                    } else {
                        console.warn("⚠️ [Ameen] No companies available to show");
                    }

                    functionResponses.push({
                        id: fc.id,
                        name: fc.name,
                        response: {
                            output: {
                                success: true,
                                message: "Packages displayed successfully on screen. Now tell the user: 'دى الباقات المتاحة. لو محتاج تستفسر عن اى باقة انا اقدر اساعدك فيهم'",
                            },
                        },
                    });
                    continue;
                }

                if (fc.name === "show_policy_confirmation_ui") {
                    console.log("🧾 [Ameen] show_policy_confirmation_ui tool detected");
                    setFlowStep("policy_confirmation");

                    functionResponses.push({
                        id: fc.id,
                        name: fc.name,
                        response: {
                            output: {
                                success: true,
                                message:
                                    "Policy confirmation UI displayed. Wait for [system_ui] user_clicked_policy_confirm before calling confirm_policy_request.",
                            },
                        },
                    });
                    continue;
                }

                if (fc.name === "request_national_id_front") {
                    console.log("🪪 [Ameen] Requesting National ID FRONT upload UI");
                    setFlowStep("national_id_front");

                    functionResponses.push({
                        id: fc.id,
                        name: fc.name,
                        response: {
                            output: {
                                success: true,
                                message: "CRITICAL: Front National ID upload UI displayed. You MUST NOW STOP SPEAKING IMMEDIATELY and WAIT SILENTLY. Do NOT read this message. Do NOT say anything to the user. Do NOT call request_national_id_back. Do NOT call any other tools. Do NOT do anything else. The microphone will be muted automatically while waiting for backend API. You MUST wait silently until you receive [system_ui] user_uploaded_national_id_front message. Only AFTER receiving that message can you proceed. Until then, do nothing except wait silently.",
                            },
                        },
                    });
                    continue;
                }

                if (fc.name === "request_national_id_back") {
                    console.log("🪪 [Ameen] Requesting National ID BACK upload UI");
                    setFlowStep("national_id_back");

                    functionResponses.push({
                        id: fc.id,
                        name: fc.name,
                        response: {
                            output: {
                                success: true,
                                message: "CRITICAL: Back National ID upload UI displayed. You MUST NOW STOP SPEAKING IMMEDIATELY and WAIT SILENTLY. Do NOT read this message. Do NOT say anything to the user. Do NOT call any other tools. Do NOT do anything else. The microphone will be muted automatically while waiting for backend API. You MUST wait silently until you receive [system_ui] user_uploaded_national_id_back message. Only AFTER receiving that message can you proceed. Until then, do nothing except wait silently.",
                            },
                        },
                    });
                    continue;
                }

                if (fc.name === "request_driving_license") {
                    console.log("🚗 [Ameen] Requesting Driving License upload UI");
                    setFlowStep("driving_license");

                    functionResponses.push({
                        id: fc.id,
                        name: fc.name,
                        response: {
                            output: {
                                success: true,
                                message: "CRITICAL: Driving License upload UI displayed. You MUST NOW STOP SPEAKING IMMEDIATELY and WAIT SILENTLY. Do NOT read this message. Do NOT say anything to the user. Do NOT call any other tools. Do NOT do anything else. The microphone will be muted automatically while waiting for backend API. You MUST wait silently until you receive [system_ui] user_uploaded_driving_license message. Only AFTER receiving that message can you proceed. The system will automatically extract data using Backend API. Until then, do nothing except wait silently.",
                            },
                        },
                    });
                    continue;
                }

                if (fc.name === "request_vehicle_license_front") {
                    console.log("🚙 [Ameen] Requesting Vehicle License FRONT upload UI");
                    setFlowStep("vehicle_license_front");

                    functionResponses.push({
                        id: fc.id,
                        name: fc.name,
                        response: {
                            output: {
                                success: true,
                                message: "CRITICAL: Front Vehicle License upload UI displayed. You MUST NOW STOP SPEAKING IMMEDIATELY and WAIT SILENTLY. Do NOT read this message. Do NOT say anything to the user. Do NOT call any other tools. Do NOT call request_vehicle_license_back. Do NOT do anything else. The microphone will be muted automatically while waiting for backend API. You MUST wait silently until you receive [system_ui] user_uploaded_vehicle_license_front message. Only AFTER receiving that message can you proceed. The system will automatically extract data using Backend API. Until then, do nothing except wait silently.",
                            },
                        },
                    });
                    continue;
                }

                if (fc.name === "request_vehicle_license_back") {
                    console.log("🚙 [Ameen] Requesting Vehicle License BACK upload UI");
                    setFlowStep("vehicle_license_back");

                    functionResponses.push({
                        id: fc.id,
                        name: fc.name,
                        response: {
                            output: {
                                success: true,
                                message: "CRITICAL: Back Vehicle License upload UI displayed. You MUST NOW STOP SPEAKING IMMEDIATELY and WAIT SILENTLY. Do NOT read this message. Do NOT say anything to the user. Do NOT call any other tools. Do NOT do anything else. The microphone will be muted automatically while waiting for backend API. You MUST wait silently until you receive [system_ui] user_uploaded_vehicle_license_back message. Only AFTER receiving that message can you proceed. The system will automatically extract data using Backend API. Until then, do nothing except wait silently.",
                            },
                        },
                    });
                    continue;
                }

                if (fc.name === "submit_policy_request") {
                    console.log("📋 [Ameen] submit_policy_request tool detected!");
                    try {
                        console.log("📨 [Ameen] Calling backend submit-request");

                        const result = await submitPolicyRequest();
                        console.log("✅ [Ameen] Policy request submitted successfully:", result);

                        // Send response to Gemini immediately
                        functionResponses.push({
                            id: fc.id,
                            name: fc.name,
                            response: {
                                output: {
                                    success: true,
                                    requestId: result.requestId,
                                    urlDraft: result.urlDraft,
                                    message: `Policy request submitted successfully. Request ID: ${result.requestId}. STOP. DO NOT call any other tools. Wait for system instruction.`,
                                },
                            },
                        });

                        // Show policy number in modal for user to save
                        if (onShowPolicyNumber && result.requestId) {
                            onShowPolicyNumber(
                                result.requestId,
                                "رقم طلب الوثيقة",
                                "تم تسجيل طلب الوثيقة بنجاح. يرجى حفظ رقم الطلب:",
                                () => {
                                    // User confirmed - modal will close automatically
                                    console.log("✅ [Ameen] User confirmed policy request number");
                                    
                                    // Send system_ui signal that user confirmed
                                    client.send(
                                        [{ text: "[system_ui] user_confirmed_policy_request_number" }],
                                        false
                                    );
                                    
                                    // Send next instruction to show confirmation UI
                                    setTimeout(() => {
                                        client.send(
                                            [
                                                {
                                                    text: "[SYSTEM INSTRUCTION] Policy request number confirmed. Now call show_policy_confirmation_ui to show the policy summary and confirmation button.",
                                                },
                                            ],
                                            true
                                        );
                                        console.log("📤 [Ameen] Sent instruction: show_policy_confirmation_ui");
                                    }, 300);
                                }
                            );
                        }
                    } catch (error: any) {
                        console.error("❌ [Ameen] Error submitting policy request:", error);
                        functionResponses.push({
                            id: fc.id,
                            name: fc.name,
                            response: {
                                output: {
                                    success: false,
                                    error: error.message || "Failed to submit policy request",
                                },
                            },
                        });
                    }
                    continue;
                }

                if (fc.name === "confirm_policy_request") {
                    console.log("📄 [Ameen] confirm_policy_request tool detected!");
                    const args = fc.args as any;
                    const requestId = args.requestId as string;

                    try {
                        if (!requestId) {
                            throw new Error(
                                "Missing required requestId parameter for confirm_policy_request"
                            );
                        }

                        console.log(
                            "📨 [Ameen] Calling backend confirm policy request with:",
                            requestId
                        );

                        const message = await confirmPolicyRequest(requestId);

                        console.log(
                            "✅ [Ameen] Policy request confirmed successfully:",
                            message
                        );

                        // Always return response to AI immediately
                        functionResponses.push({
                            id: fc.id,
                            name: fc.name,
                            response: {
                                output: {
                                    success: true,
                                    requestId,
                                    message,
                                },
                            },
                        });

                        // Detect payment link and show it to the user
                        const urlRegex = /(https?:\/\/[^\s]+)/i;
                        const linkMatch = message.match(urlRegex);
                        const payLink = linkMatch ? linkMatch[0] : message;

                        if (onShowPolicyNumber && payLink) {
                            onShowPolicyNumber(
                                payLink,
                                "رابط الدفع",
                                "من فضلك ادفع من خلال الرابط التالي لاستكمال طلبك:",
                                () => {
                                    console.log("✅ [Ameen] User acknowledged payment link");
                                }
                            );
                        }
                    } catch (error: any) {
                        console.error(
                            "❌ [Ameen] Error confirming policy request:",
                            error
                        );
                        functionResponses.push({
                            id: fc.id,
                            name: fc.name,
                            response: {
                                output: {
                                    success: false,
                                    error:
                                        error?.message ||
                                        "Failed to confirm policy request. Please try again later.",
                                },
                            },
                        });
                    }
                    continue;
                }

                if (fc.name === "save_national_id_data") {
                    console.log("💾 [Ameen] Saving extracted National ID data");
                    const args = fc.args as any;

                    if (setCustomerNationalIdData) {
                        setCustomerNationalIdData((prev) => {
                            const updated = {
                                ...prev,
                                // Only update fields that are provided (merge with existing data)
                                ...(args.national_id_number && { national_id_number: args.national_id_number }),
                                ...(args.profession && { profession: args.profession }),
                                ...(args.gender && { gender: args.gender }),
                                ...(args.religion && { religion: args.religion }),
                                ...(args.marital_status && { marital_status: args.marital_status }),
                                ...(args.expiration_date && { expiration_date: args.expiration_date }),
                                ...(args.issue_batch_date && { issue_batch_date: args.issue_batch_date }),
                                ...(args.full_name && { full_name: args.full_name }),
                                ...(args.address && { address: args.address }),
                                ...(args.birth_date && { birth_date: args.birth_date }),
                                ...(args.card_serial_number && { card_serial_number: args.card_serial_number }),
                            };

                            // Save to session storage (incremental - updates as data is extracted)
                            try {
                                const existingData = sessionStorage.getItem("saaed_user_data");
                                const userData = existingData ? JSON.parse(existingData) : {};

                                // Extract only text fields (exclude base64 images to avoid quota issues)
                                const textOnlyData = {
                                    national_id_number: updated.national_id_number,
                                    profession: updated.profession,
                                    gender: updated.gender,
                                    religion: updated.religion,
                                    marital_status: updated.marital_status,
                                    expiration_date: updated.expiration_date,
                                    issue_batch_date: updated.issue_batch_date,
                                    full_name: updated.full_name,
                                    address: updated.address,
                                    birth_date: updated.birth_date,
                                    card_serial_number: updated.card_serial_number,
                                };

                                const updatedUserData = {
                                    ...userData,
                                    customerNationalIdData: textOnlyData,
                                };

                                sessionStorage.setItem(
                                    "saaed_user_data",
                                    JSON.stringify(updatedUserData)
                                );
                                console.log(
                                    "✅ [Ameen] Customer National ID data (text only) saved to session storage"
                                );
                            } catch (error) {
                                console.error(
                                    "❌ [Ameen] Failed to save to session storage:",
                                    error
                                );
                            }

                            return updated;
                        });
                    }

                    functionResponses.push({
                        id: fc.id,
                        name: fc.name,
                        response: {
                            output: {
                                success: true,
                                message: "National ID data saved successfully.",
                            },
                        },
                    });
                    continue;
                }

                // ═══════════════════════════════════════════════════════════
                // VEHICLE SURVEY TOOLS
                // ═══════════════════════════════════════════════════════════

                if (fc.name === "start_camera_stream") {
                    console.log("📹 [Ameen] start_camera_stream tool detected!");
                    try {
                        if (!onStartCameraStream) {
                            throw new Error("Camera control not available");
                        }

                        const success = await onStartCameraStream();

                        if (success) {
                            // Check currentFlowTypeRef to determine if we're in claim or policy flow
                            const isClaimFlow = currentFlowTypeRef.current === 'claim';
                            
                            if (isClaimFlow) {
                                setFlowStep("claim_vehicle_survey");
                                console.log("✅ [Ameen] Camera stream started for CLAIM flow");
                            } else {
                                setFlowStep("vehicle_survey");
                                currentFlowTypeRef.current = 'policy'; // Ensure policy flow type is set
                                console.log("✅ [Ameen] Camera stream started for POLICY flow");
                            }
                            console.log("✅ [Ameen] Camera stream started successfully");

                            functionResponses.push({
                                id: fc.id,
                                name: fc.name,
                                response: {
                                    output: {
                                        status: "camera_stream_started",
                                        message: "Camera stream initialized successfully. You can now analyze the video frames to detect the vehicle sides. Start with Front, then Right, Back, and finally Left.",
                                    },
                                },
                            });
                        } else {
                            throw new Error("Failed to start camera stream");
                        }
                    } catch (error: any) {
                        console.error("❌ [Ameen] Error starting camera stream:", error);
                        functionResponses.push({
                            id: fc.id,
                            name: fc.name,
                            response: {
                                output: {
                                    status: "error",
                                    error_code: "CAMERA_NOT_AVAILABLE",
                                    message: error.message || "Unable to start camera stream.",
                                },
                            },
                        });
                    }
                    continue;
                }

                if (fc.name === "take_vehicle_picture") {
                    console.log("📸 [Ameen] take_vehicle_picture tool detected!");
                    const args = fc.args as any;
                    const side = args.side as VehicleSide;

                    try {
                        if (!onTakeVehiclePicture) {
                            throw new Error("Picture capture not available");
                        }

                        if (!side || !["Front", "Right", "Back", "Left"].includes(side)) {
                            throw new Error(`Invalid side: ${side}. Must be Front, Right, Back, or Left.`);
                        }

                        const imageData = await onTakeVehiclePicture(side);

                        if (imageData) {
                            console.log(`✅ [Ameen] Picture captured for side: ${side}`);

                            functionResponses.push({
                                id: fc.id,
                                name: fc.name,
                                response: {
                                    output: {
                                        status: "picture_taken",
                                        side: side,
                                        message: `Image captured successfully for ${side} side.`,
                                    },
                                },
                            });
                        } else {
                            throw new Error("Failed to capture image");
                        }
                    } catch (error: any) {
                        console.error("❌ [Ameen] Error capturing vehicle picture:", error);
                        functionResponses.push({
                            id: fc.id,
                            name: fc.name,
                            response: {
                                output: {
                                    status: "error",
                                    error_code: "CAPTURE_FAILED",
                                    message: error.message || "Unable to capture image. Try again.",
                                },
                            },
                        });
                    }
                    continue;
                }

                if (fc.name === "request_close_camera") {
                    console.log("📹 [Ameen] request_close_camera tool detected!");
                    try {
                        if (onCloseCameraStream) {
                            onCloseCameraStream();
                        }

                        // Hide the survey status component when camera closes
                        setFlowStep("idle");

                        console.log("✅ [Ameen] Camera stream closed successfully");

                        functionResponses.push({
                            id: fc.id,
                            name: fc.name,
                            response: {
                                output: {
                                    status: "camera_stream_closed",
                                    message: "Camera stream closed successfully. STOP. DO NOT call any other tools. Wait for system instruction.",
                                },
                            },
                        });

                        // Send dynamic instruction to analyze images after camera closes
                        // Determine which analyze tool to call based on flow type
                        const analyzeToolName = currentFlowTypeRef.current === 'claim' 
                            ? 'analyze_claim_vehicle_images' 
                            : 'analyze_policy_vehicle_images';
                        
                        setTimeout(() => {
                            client.send(
                                [{
                                    text: `[SYSTEM INSTRUCTION] Camera closed successfully. Say 'ثانية واحدة هحلل صور العربيه' then call ${analyzeToolName} to analyze the vehicle condition.`
                                }],
                                true
                            );
                            console.log(`📤 [Ameen] Sent instruction to analyze images using ${analyzeToolName}`);
                        }, 300);
                    } catch (error: any) {
                        console.error("❌ [Ameen] Error closing camera stream:", error);
                        functionResponses.push({
                            id: fc.id,
                            name: fc.name,
                            response: {
                                output: {
                                    status: "error",
                                    error_code: "CAMERA_CLOSE_FAILED",
                                    message: error.message || "Unable to close camera stream.",
                                },
                            },
                        });
                    }
                    continue;
                }

                if (fc.name === "show_policy_survey_options") {
                    console.log("📋 [Ameen] show_policy_survey_options tool detected!");
                    console.log("🔄 [Ameen] Setting flowStep to 'policy_survey_options'");
                    setFlowStep("policy_survey_options");
                    currentFlowTypeRef.current = 'policy'; // Set flow type for policy flow
                    console.log("✅ [Ameen] flowStep set to 'policy_survey_options' - UI should now display survey options");

                    functionResponses.push({
                        id: fc.id,
                        name: fc.name,
                        response: {
                            output: {
                                success: true,
                                message: "CRITICAL: Policy survey options displayed (Live Survey / Upload Images). You MUST NOW STOP and WAIT. Do NOT call any other tools. You MUST wait until you receive either [system_ui] user_selected_policy_live_survey or [system_ui] user_selected_policy_upload_images. Only AFTER receiving one of these messages can you proceed.",
                            },
                        },
                    });
                    continue;
                }

                if (fc.name === "request_policy_vehicle_images") {
                    console.log("📋 [Ameen] request_policy_vehicle_images tool detected!");
                    setFlowStep("policy_vehicle_upload");

                    functionResponses.push({
                        id: fc.id,
                        name: fc.name,
                        response: {
                            output: {
                                success: true,
                                message: "CRITICAL: Policy vehicle images upload component displayed (4 images: Front, Right, Back, Left). You MUST NOW STOP and WAIT. Do NOT call any other tools. You MUST wait until you receive [system_ui] user_uploaded_policy_vehicle_images message with the images and damage description. Only AFTER receiving that message can you proceed.",
                            },
                        },
                    });
                    continue;
                }

                if (fc.name === "analyze_policy_vehicle_images" || fc.name === "analyze_claim_vehicle_images") {
                    const isClaimFlow = fc.name === "analyze_claim_vehicle_images";
                    console.log(`🔍 [Ameen] ${fc.name} tool detected! (${isClaimFlow ? 'CLAIM' : 'POLICY'} flow)`);
                    
                    // Get images based on flow type
                    let images: VehicleSurveyImage[] = [];
                    
                    if (isClaimFlow && getClaimSurveyImages) {
                        images = getClaimSurveyImages();
                        console.log("📸 [Ameen] Retrieved images from claim survey for analysis:", images.length);
                    } else if (!isClaimFlow && getSurveyImages) {
                        images = getSurveyImages();
                        console.log("📸 [Ameen] Retrieved images from policy survey for analysis:", images.length);
                    }
                    
                    try {

                        console.log("📸 [Ameen] Analyzing vehicle images:", {
                            imageCount: images.length,
                            images: images.map(img => ({
                                side: img.side,
                                hasData: !!img.data,
                            }))
                        });

                        if (images.length === 0) {
                            throw new Error("No vehicle images found for analysis. Please capture vehicle images first.");
                        }

                        // Validate that all images have data
                        const imagesWithData = images.filter(img => img.data && img.data.trim().length > 0);
                        if (imagesWithData.length === 0) {
                            throw new Error("Vehicle images are empty. Please capture vehicle images again.");
                        }

                        console.log("📨 [Ameen] Calling Gemini API to analyze vehicle images...");

                        // Mute microphone while waiting for backend API
                        if (onMuteMic) {
                            console.log("🔇 [Ameen] Muting microphone while waiting for vehicle analysis API");
                            onMuteMic();
                        }

                        // Call the backend API to analyze images
                        const result = await analyzeVehicleImages(imagesWithData);

                        console.log("✅ [Ameen] Vehicle analysis completed successfully");

                        // Unmute microphone after backend API completes
                        if (onUnmuteMic) {
                            console.log("🔊 [Ameen] Unmuting microphone after vehicle analysis API completion");
                            onUnmuteMic();
                        }

                        functionResponses.push({
                            id: fc.id,
                            name: fc.name,
                            response: {
                                output: {
                                    success: true,
                                    vehicleAnalyzeDescription: result.vehicleAnalyzeDescription,
                                    message: `Vehicle analysis completed successfully. Here is the description:\n\n${result.vehicleAnalyzeDescription}\n\n**CRITICAL**: Read this description naturally to the user in Egyptian Arabic. After you finish reading and the audio playback completes, STOP. DO NOT call any other tools. Wait for system signal.\n\n**VERBAL CONFIRMATIONS ARE INVALID**: If the user says "تمام", "اوكى", "ماشى", or any similar verbal confirmation, IGNORE it completely. Do NOT read any system instruction. Wait silently for the next system instruction.`,
                                },
                            },
                        });

                        // Set flag to wait for audio completion
                        waitingForDescriptionAudioRef.current = true;
                        vehicleDescriptionRef.current = result.vehicleAnalyzeDescription;
                        currentFlowTypeRef.current = isClaimFlow ? 'claim' : 'policy';
                        console.log(`⏳ [Ameen] Waiting for vehicle description audio to complete... (${currentFlowTypeRef.current} flow)`);
                    } catch (error: any) {
                        console.error("❌ [Ameen] Error analyzing vehicle images:", error);
                        
                        // Unmute microphone even if API fails
                        if (onUnmuteMic) {
                            console.log("🔊 [Ameen] Unmuting microphone after vehicle analysis API error");
                            onUnmuteMic();
                        }
                        
                        // Use fallback description on error
                        const fallbackDescription = "السيارة بحالة جيده";
                        
                        functionResponses.push({
                            id: fc.id,
                            name: fc.name,
                            response: {
                                output: {
                                    success: true,
                                    vehicleAnalyzeDescription: fallbackDescription,
                                    message: `Vehicle analysis completed. Here is the description:\n\n${fallbackDescription}\n\n**CRITICAL**: Read this description naturally to the user in Egyptian Arabic. After you finish reading and the audio playback completes, STOP. DO NOT call any other tools. Wait for system signal.\n\n**VERBAL CONFIRMATIONS ARE INVALID**: If the user says "تمام", "اوكى", "ماشى", or any similar verbal confirmation, IGNORE it completely. Do NOT read any system instruction. Wait silently for the next system instruction.`,
                                },
                            },
                        });

                        // Set flag to wait for audio completion
                        waitingForDescriptionAudioRef.current = true;
                        vehicleDescriptionRef.current = fallbackDescription;
                        currentFlowTypeRef.current = isClaimFlow ? 'claim' : 'policy';
                        console.log(`⏳ [Ameen] Waiting for vehicle description audio to complete... (${currentFlowTypeRef.current} flow)`);
                    }
                    continue;
                }

                if (fc.name === "submit_policy_survey_request" || fc.name === "submit_claim_survey_request") {
                    const isClaimSurvey = fc.name === "submit_claim_survey_request";
                    console.log(`📋 [Ameen] ${fc.name} tool detected! (${isClaimSurvey ? 'CLAIM' : 'POLICY'} flow)`);

                    // CRITICAL: Prevent duplicate submissions
                    if (surveySubmittedRef.current) {
                        console.warn("⚠️ [Ameen] Survey already submitted! Ignoring duplicate call.");
                        functionResponses.push({
                            id: fc.id,
                            name: fc.name,
                            response: {
                                output: {
                                    status: "already_submitted",
                                    error_code: "DUPLICATE_SUBMISSION_PREVENTED",
                                    message: "Survey has already been submitted successfully.",
                                },
                            },
                        });
                        continue;
                    }

                    const args = fc.args as any;
                    const damageDescription = args.damage_description as string;

                    try {
                        if (!damageDescription) {
                            throw new Error("Missing required damage_description parameter");
                        }

                        // Detect which flow based on which images are available
                        let images: VehicleSurveyImage[] = [];
                        let isClaimFlow = false;
                        
                        if (getClaimSurveyImages) {
                            const claimImages = getClaimSurveyImages();
                            console.log("📸 [Ameen] Available images from claim survey:", claimImages.length);
                            if (claimImages.length > 0) {
                                images = claimImages;
                                isClaimFlow = true;
                                currentFlowTypeRef.current = 'claim';
                            }
                        }
                        
                        // If no claim images, try policy survey images
                        if (images.length === 0 && getSurveyImages) {
                            images = getSurveyImages();
                            console.log("📸 [Ameen] Retrieved images from policy survey:", images.length);
                            isClaimFlow = false;
                            currentFlowTypeRef.current = 'policy';
                        }
                        
                        console.log("📸 [Ameen] Using images from:", isClaimFlow ? "CLAIM flow" : "POLICY flow", "- Count:", images.length);

                        console.log("📸 [Ameen] Survey images retrieved:", {
                            imageCount: images.length,
                            images: images.map(img => ({
                                side: img.side,
                                hasData: !!img.data,
                                dataLength: img.data ? img.data.length : 0,
                                dataPreview: img.data ? img.data.substring(0, 50) : "no data"
                            }))
                        });

                        if (images.length === 0) {
                            throw new Error("No survey images found. Please capture vehicle images before submitting the survey.");
                        }

                        // Validate that all images have data
                        const imagesWithData = images.filter(img => img.data && img.data.trim().length > 0);
                        if (imagesWithData.length === 0) {
                            throw new Error("Survey images are empty. Please capture vehicle images again.");
                        }

                        if (imagesWithData.length < images.length) {
                            console.warn(`⚠️ [Ameen] ${images.length - imagesWithData.length} images are empty, using ${imagesWithData.length} valid images`);
                        }

                        console.log("📨 [Ameen] Submitting survey with:", {
                            imageCount: imagesWithData.length,
                            damageDescriptionLength: damageDescription.length,
                        });

                        // Mark as submitted BEFORE the API call to prevent race conditions
                        surveySubmittedRef.current = true;

                        const result = await submitSurveyRequest({
                            images: imagesWithData,
                            damageDescription,
                        });

                        console.log("✅ [Ameen] Survey submitted successfully:", result);

                        // Reset flow step after successful submission
                        setFlowStep("idle");

                        functionResponses.push({
                            id: fc.id,
                            name: fc.name,
                            response: {
                                output: {
                                    status: "survey_submitted",
                                    surveyId: result.surveyId,
                                    message: result.message,
                                },
                            },
                        });

                        // Send next instruction based on which tool was called
                        console.log("🔍 [Ameen] Survey tool called:", fc.name);
                        setTimeout(() => {
                            if (isClaimSurvey) {
                                // Claim Flow: Go to workshop selection
                                console.log("📤 [Ameen] Sending instruction for CLAIM flow: show_workshop_options");
                                client.send(
                                    [
                                        {
                                            text: "[SYSTEM INSTRUCTION] Survey submitted successfully. Now say 'تمت المعاينة. تحب تصلح فين؟' then call show_workshop_options to let the user choose a workshop.",
                                        },
                                    ],
                                    true
                                );
                            } else {
                                // Policy Flow: Go to policy submission
                                console.log("📤 [Ameen] Sending instruction for POLICY flow: submit_policy_request");
                                client.send(
                                    [
                                        {
                                            text: "[SYSTEM INSTRUCTION] Survey submitted successfully. Now call submit_policy_request to submit the policy.",
                                        },
                                    ],
                                    true
                                );
                            }
                        }, 300);
                    } catch (error: any) {
                        console.error("❌ [Ameen] Error submitting survey:", error);
                        // Reset flag on error so user can retry
                        surveySubmittedRef.current = false;
                        functionResponses.push({
                            id: fc.id,
                            name: fc.name,
                            response: {
                                output: {
                                    status: "error",
                                    error_code: "SURVEY_SUBMISSION_FAILED",
                                    message: error.message || "Survey could not be submitted. Try again later.",
                                },
                            },
                        });
                    }
                    continue;
                }

                // Handle FAQ search with vector embeddings
                if (fc.name === "search_faq") {
                    console.log("🔍 [Ameen] ========== FAQ SEARCH TOOL CALLED ==========");
                    console.log("🔍 [Ameen] Raw args:", fc.args);
                    console.log("🔍 [Ameen] Args JSON:", JSON.stringify(fc.args, null, 2));

                    // Extract question from args - handle various possible formats
                    let query = (fc.args as any)?.question;

                    // If question is not found, try other possible parameter names
                    if (!query) {
                        query = (fc.args as any)?.query || (fc.args as any)?.q || (fc.args as any)?.text;
                        if (query) {
                            console.log(`⚠️ [Ameen] Found query under different parameter name`);
                        }
                    }

                    if (!query || typeof query !== 'string' || query.trim().length === 0) {
                        console.error("❌ [Ameen] Invalid FAQ search query:", query, "Full args:", fc.args);
                        functionResponses.push({
                            id: fc.id,
                            name: fc.name,
                            response: {
                                output: {
                                    success: false,
                                    error: "معذرة، لم أستطع فهم السؤال. من فضلك حاول مرة أخرى. / Sorry, I couldn't understand the question. Please try again.",
                                    message: "The question parameter was empty or invalid.",
                                },
                            },
                        });
                        continue;
                    }

                    query = query.trim();
                    console.log(`📝 [Ameen] Search query: "${query}"`);

                    // Detect language from query (simple heuristic)
                    const hasArabic = /[\u0600-\u06FF]/.test(query);
                    const language = hasArabic ? 'ar' : 'en';

                    console.log(`🌐 [Ameen] Detected language: ${language}`);

                    // Perform vector search
                    try {
                        console.log(`🔎 [Ameen] Starting vector search...`);

                        // Initialize service if needed (defensive check)
                        await vectorFAQService.initialize();

                        // Lower threshold to 0.25 for better matching
                        const results = await vectorFAQService.search(query, language, 5, 0.25);
                        console.log(`📊 [Ameen] Search completed. Found ${results.length} results`);

                        if (results.length > 0) {
                            // Format results for AI
                            const formattedResults = vectorFAQService.formatResultsForAI(results, language);
                            console.log(`📄 [Ameen] Formatted results preview:`);
                            console.log(formattedResults.substring(0, 500) + "...");

                            // Get the first result's answer for verification
                            const firstAnswer = language === 'ar' ? results[0].item.answer : results[0].item.answer_en;
                            const firstQuestion = language === 'ar' ? results[0].item.question : results[0].item.question_en;
                            console.log(`🎯 [Ameen] Best match (${(results[0].similarity * 100).toFixed(1)}%):`);
                            console.log(`   Q: ${firstQuestion}`);
                            console.log(`   A: ${firstAnswer}`);

                            functionResponses.push({
                                id: fc.id,
                                name: fc.name,
                                response: {
                                    output: {
                                        success: true,
                                        found: true,
                                        results: formattedResults,
                                        count: results.length,
                                        bestMatch: {
                                            question: firstQuestion,
                                            answer: firstAnswer,
                                            confidence: results[0].similarity,
                                        },
                                        instruction: language === 'ar'
                                            ? "استخدم الإجابة أعلاه للرد على المستخدم. لا تخترع معلومات إضافية."
                                            : "Use the answer above to respond to the user. Do not invent additional information.",
                                    },
                                },
                            });

                            console.log(`✅ [Ameen] Function response prepared with ${results.length} results`);
                        } else {
                            console.log(`⚠️ [Ameen] No results found for query: "${query}"`);
                            const noResultsMessage = language === 'ar'
                                ? `لم يتم العثور على نتائج محددة في قاعدة المعرفة للسؤال: "${query}". يمكنك استخدام معلوماتك العامة.`
                                : `No specific results found in the knowledge base for: "${query}". You may use your general knowledge.`;

                            functionResponses.push({
                                id: fc.id,
                                name: fc.name,
                                response: {
                                    output: {
                                        success: true,
                                        found: false,
                                        results: noResultsMessage,
                                        count: 0,
                                        instruction: "No results in knowledge base. Answer from general knowledge if appropriate.",
                                    },
                                },
                            });
                        }
                    } catch (error) {
                        console.error('❌ [Ameen] Vector FAQ search error:', error);
                        console.error('❌ [Ameen] Error details:', {
                            message: error instanceof Error ? error.message : 'Unknown error',
                            stack: error instanceof Error ? error.stack : 'No stack trace',
                            name: error instanceof Error ? error.name : 'Unknown',
                        });

                        // Return a graceful error response
                        const errorMessage = language === 'ar'
                            ? 'حدث خطأ أثناء البحث في قاعدة المعرفة. يمكنك الإجابة من معلوماتك العامة.'
                            : 'An error occurred while searching the knowledge base. You may answer from general knowledge.';

                        functionResponses.push({
                            id: fc.id,
                            name: fc.name,
                            response: {
                                output: {
                                    success: false,
                                    found: false,
                                    error: errorMessage,
                                    technicalError: error instanceof Error ? error.message : 'Search failed',
                                },
                            },
                        });
                    }

                    console.log("🔍 [Ameen] ========== FAQ SEARCH COMPLETE ==========");
                    continue;
                }

                // ═══════════════════════════════════════════════════════════
                // CLAIM REQUEST TOOLS
                // ═══════════════════════════════════════════════════════════

                if (fc.name === "request_policy_inquiry") {
                    console.log("📋 [Ameen] request_policy_inquiry tool detected!");
                    setFlowStep("claim_policy_inquiry");

                    functionResponses.push({
                        id: fc.id,
                        name: fc.name,
                        response: {
                            output: {
                                success: true,
                                message: "CRITICAL: Policy inquiry popup displayed. You MUST NOW STOP and WAIT. Do NOT call any other tools. You MUST wait until you receive [system_ui] user_submitted_policy_inquiry message with the policy data. Only AFTER receiving that message can you proceed.",
                            },
                        },
                    });
                    continue;
                }

                if (fc.name === "show_claim_survey_options") {
                    console.log("📋 [Ameen] show_claim_survey_options tool detected!");
                    console.log("🔄 [Ameen] Setting flowStep to 'claim_survey_options'");
                    setFlowStep("claim_survey_options");
                    currentFlowTypeRef.current = 'claim'; // Set flow type for claim flow
                    console.log("✅ [Ameen] flowStep set to 'claim_survey_options' - UI should now display survey options");

                    functionResponses.push({
                        id: fc.id,
                        name: fc.name,
                        response: {
                            output: {
                                success: true,
                                message: "CRITICAL: Survey options displayed (Live Survey / Upload Images). You MUST NOW STOP and WAIT. Do NOT call any other tools. You MUST wait until you receive either [system_ui] user_selected_live_survey or [system_ui] user_selected_upload_images. Only AFTER receiving one of these messages can you proceed.",
                            },
                        },
                    });
                    continue;
                }

                if (fc.name === "request_claim_vehicle_images") {
                    console.log("📋 [Ameen] request_claim_vehicle_images tool detected!");
                    setFlowStep("claim_vehicle_upload");

                    functionResponses.push({
                        id: fc.id,
                        name: fc.name,
                        response: {
                            output: {
                                success: true,
                                message: "CRITICAL: Vehicle images upload component displayed (4 images: Front, Right, Back, Left). You MUST NOW STOP and WAIT. Do NOT call any other tools. You MUST wait until you receive [system_ui] user_uploaded_claim_vehicle_images message with the images and damage description. Only AFTER receiving that message can you proceed.",
                            },
                        },
                    });
                    continue;
                }


                if (fc.name === "show_workshop_options") {
                    console.log("📋 [Ameen] show_workshop_options tool detected!");
                    setFlowStep("claim_workshop_options");

                    functionResponses.push({
                        id: fc.id,
                        name: fc.name,
                        response: {
                            output: {
                                success: true,
                                message: "CRITICAL: Workshop options displayed (Contracted / Non-Contracted). You MUST NOW STOP and WAIT. Do NOT call any other tools. You MUST wait until you receive either [system_ui] user_selected_contracted_workshop or [system_ui] user_selected_non_contracted_workshop. Only AFTER receiving one of these messages can you proceed.",
                            },
                        },
                    });
                    continue;
                }

                if (fc.name === "show_contracted_workshops_list") {
                    console.log("📋 [Ameen] show_contracted_workshops_list tool detected!");

                    // Open popup immediately without waiting for async operations
                    setFlowStep("claim_contracted_workshops");
                    
                    // Start async operations in background
                    (async () => {
                        try {
                            // Get user's real current location - required for accurate distance calculation
                            console.log("📍 [Ameen] Requesting user location...");
                            let userLocation;
                            try {
                                userLocation = await getCurrentLocation();
                                console.log("✅ [Ameen] User location obtained:", userLocation.lat, userLocation.long);
                            } catch (locationError: any) {
                                console.error("❌ [Ameen] Failed to get user location:", locationError.message);
                                // Don't throw error, just log it - popup is already open
                                return;
                            }

                            // Validate location coordinates
                            if (!userLocation || typeof userLocation.lat !== 'number' || typeof userLocation.long !== 'number') {
                                console.error("❌ [Ameen] Invalid location data received");
                                return;
                            }

                            if (isNaN(userLocation.lat) || isNaN(userLocation.long)) {
                                console.error("❌ [Ameen] Location coordinates are not valid numbers");
                                return;
                            }

                            console.log("📨 [Ameen] Fetching contracted workshops from backend with user location:", userLocation.lat, userLocation.long);
                            const workshops = await getContractedWorkshops(userLocation.lat, userLocation.long);
                            console.log("✅ [Ameen] Contracted workshops fetched:", workshops.length);

                            if (setContractedWorkshops) {
                                setContractedWorkshops(workshops);
                            }

                            // Find nearest workshop and send message to AI
                            const nearestWorkshop = workshops.find(w => w.isNearest);
                            if (nearestWorkshop) {
                                console.log("📍 [Ameen] Nearest workshop found:", nearestWorkshop.workshopName);
                                // Send message to AI about the nearest workshop
                                setTimeout(() => {
                                    const distanceText = nearestWorkshop.distance 
                                        ? ` على بعد ${nearestWorkshop.distance < 1000 ? Math.round(nearestWorkshop.distance) + ' متر' : (nearestWorkshop.distance / 1000).toFixed(1) + ' كيلومتر'}`
                                        : '';
                                    client.send(
                                        [
                                            {
                                                text: `[SYSTEM INFO - READ TO USER] الورشة الاقرب ليك هى ${nearestWorkshop.workshopName}${distanceText}. اقرأ هذه المعلومة للمستخدم بصوت عالٍ قبل عرض قائمة الورش. قل: "الورشة الاقرب ليك هى ${nearestWorkshop.workshopName}${distanceText}"`,
                                            },
                                        ],
                                        true // turnComplete: true to trigger AI response
                                    );
                                    console.log("📤 [Ameen] Sent nearest workshop info to AI:", nearestWorkshop.workshopName);
                                }, 500);
                            }
                        } catch (error: any) {
                            console.error("❌ [Ameen] Error fetching contracted workshops:", error);
                        }
                    })();

                    functionResponses.push({
                        id: fc.id,
                        name: fc.name,
                        response: {
                            output: {
                                success: true,
                                message: `CRITICAL: Contracted workshops popup displayed. Loading workshops... You MUST NOW STOP and WAIT. Do NOT call any other tools. You MUST wait until you receive [system_ui] user_chose_contracted_workshop message with the selected workshop. Only AFTER receiving that message can you proceed.`,
                            },
                        },
                    });
                    continue;
                }

                if (fc.name === "show_non_contracted_workshop_form") {
                    console.log("📋 [Ameen] show_non_contracted_workshop_form tool detected!");
                    setFlowStep("claim_non_contracted_form");

                    functionResponses.push({
                        id: fc.id,
                        name: fc.name,
                        response: {
                            output: {
                                success: true,
                                message: "CRITICAL: Non-contracted workshop form displayed. You MUST NOW STOP and WAIT. Do NOT call any other tools. You MUST wait until you receive [system_ui] user_submitted_non_contracted_workshop message with the workshop details. Only AFTER receiving that message can you proceed.",
                            },
                        },
                    });
                    continue;
                }

                if (fc.name === "show_claim_number") {
                    console.log("📋 [Ameen] show_claim_number tool detected!");
                    setFlowStep("claim_number_display");

                    functionResponses.push({
                        id: fc.id,
                        name: fc.name,
                        response: {
                            output: {
                                success: true,
                                message: "CRITICAL: Claim number popup displayed. You MUST NOW STOP and WAIT. Do NOT call any other tools. You MUST wait until you receive [system_ui] user_acknowledged_claim_number. Only AFTER receiving that message can you proceed and the claim request flow will be complete.",
                            },
                        },
                    });
                    continue;
                }

                // Default handler for unknown tools
                functionResponses.push({
                    id: fc.id,
                    name: fc.name,
                    response: { output: { success: true } },
                });
            }

            if (functionResponses.length > 0) {
                console.log(
                    "📤 [Ameen] Sending tool responses to Gemini:",
                    functionResponses
                );
                client.sendToolResponse({ functionResponses });
            }
        };

        const onTurnComplete = () => {
            console.log("✅ [Ameen] Turn complete event received");
            
            // Check if we're waiting for vehicle description audio to finish
            if (waitingForDescriptionAudioRef.current) {
                console.log("🎤 [Ameen] Vehicle description audio finished! Checking if survey already submitted...");
                
                // Check if survey was already submitted during the audio turn
                if (surveySubmittedRef.current) {
                    console.log("⚠️ [Ameen] Survey was already submitted during audio turn. Skipping continuation signal.");
                    waitingForDescriptionAudioRef.current = false;
                    vehicleDescriptionRef.current = "";
                    return;
                }
                
                // Reset the flag
                waitingForDescriptionAudioRef.current = false;
                
                // Get the description to submit
                const descriptionToSubmit = vehicleDescriptionRef.current;
                vehicleDescriptionRef.current = "";
                
                // Determine which tool to call based on flow type
                const surveyToolName = currentFlowTypeRef.current === 'claim' 
                    ? 'submit_claim_survey_request' 
                    : 'submit_policy_survey_request';
                
                // Send a message to Ameen to proceed with the correct survey tool
                client.send(
                    {
                        text: `[SYSTEM SIGNAL] Vehicle description audio playback complete. You have successfully read the description to the user. NOW you MUST IMMEDIATELY call '${surveyToolName}' with this exact description as the damage_description parameter: "${descriptionToSubmit}"`,
                    },
                    true  // turnComplete = true to trigger AI response
                );
                
                console.log(`📤 [Ameen] Sent continuation signal to proceed with ${surveyToolName}`);
            }
        };

        console.log("🔧 [Ameen] Attaching toolcall and turncomplete event listeners");
        client.on("toolcall", onToolCall);
        client.on("turncomplete", onTurnComplete);

        // Log all available events for debugging
        console.log("🔧 [Ameen] Client events:", client.eventNames ? client.eventNames() : "unknown");

        return () => {
            console.log("🔧 [Ameen] Removing event listeners");
            client.off("toolcall", onToolCall);
            client.off("turncomplete", onTurnComplete);
        };
    }, [client, setFlowStep, setJSONString, setCompanies, setCustomerNationalIdData, onStartCameraStream, onTakeVehiclePicture, onCloseCameraStream, getSurveyImages, setClaimPolicyData, setContractedWorkshops, getClaimSurveyImages, currentCompaniesRef, onShowPolicyNumber]);
};

interface UseAltairChartEffectParams {
    embedRef: RefObject<HTMLDivElement>;
    jsonString: string;
}

export const useAltairChartEffect = ({
    embedRef,
    jsonString,
}: UseAltairChartEffectParams) => {
    useEffect(() => {
        if (embedRef.current && jsonString) {
            try {
                vegaEmbed(embedRef.current, JSON.parse(jsonString));
            } catch (err) {
                console.error("❌ [Ameen] Error rendering Altair chart:", err);
            }
        }
    }, [embedRef, jsonString]);
};


